import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { formatUserName, getDisplayName, invalidateTaskQueries } from "@/lib/task-query-utils";
import { apiRequest } from "@/lib/queryClient";
import { ActivityFeed, type ActivityRecord } from "./activity-feed";
import { TaskAttachmentsSection } from "./task-attachments-section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Save,
  Pencil,
  ExternalLink,
  Archive,
  Link2,
  X,
  History,
  FileText,
  Share,
  Copy,
  CopyPlus,
  Download,
  AlertTriangle,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import type { ExportSettings } from "@/lib/export-settings";
import { DEFAULT_EXPORT_SETTINGS } from "@/lib/export-settings";
import { formatMeetingMarkdown, generateMeetingPdf, getMeetingIncompleteFields } from "@/lib/meeting-export";
import { Link } from "wouter";
import { useTaskDetailData } from "./use-task-detail-data";
import { CommentSection } from "./comment-section";
import { TaskChecklistSection } from "./task-checklist-section";
import { TaskAnimalLinker } from "./task-animal-linker";
import { TaskInteractionLinker } from "./task-interaction-linker";
import { TaskLinkSection } from "./task-link-section";
import { TaskLabelSection } from "./task-label-section";
import { TaskDueDatePicker } from "./task-due-date-picker";
import { TaskNotesSummary } from "./task-notes-summary";
import { PriorityBadge, PRIORITY_OPTIONS } from "./priority-badge";
import { TaskAssigneeSelect } from "./task-assignee-select";
import { TaskStatusSelect } from "./task-status-select";
import { TaskPrioritySelect } from "./task-priority-select";
import { boardTypeFromContext, getBoardTypeConfig, getBoardRoutePath, getBoardEntityLabel, getBoardLabel } from "@/lib/board-types";
import { CopyToBoardDialog } from "./copy-to-board-dialog";

interface TaskPreviewPanelProps {
  taskId: string;
  boardId: string;
  onClose: () => void;
  fromContext?: string;
}

export function TaskPreviewPanel({ taskId, boardId: boardIdProp, onClose, fromContext }: TaskPreviewPanelProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copyToBoardOpen, setCopyToBoardOpen] = useState(false);

  const contextBoardType = boardTypeFromContext(fromContext);
  const copyableUrl = `/api/task-boards/copyable?sourceType=${encodeURIComponent(contextBoardType)}&excludeBoardId=${encodeURIComponent(boardIdProp)}`;
  const { data: copyableBoardsData } = useQuery<{ boards: { id: string; name: string; type: string }[] }>({
    queryKey: [copyableUrl],
    enabled: !!boardIdProp,
  });
  const hasCopyableBoards = (copyableBoardsData?.boards?.length ?? 0) > 0;

  const { data: activityData, isLoading: activityLoading } = useQuery<{ activity: ActivityRecord[] }>({
    queryKey: ["/api/tasks", taskId, "activity"],
    enabled: !!taskId,
  });

  const {
    task,
    isLoading,
    members,
    activeStatuses,
    availableLabels,
    isOverdue,
    canEdit,
    canComment,
    userRole,
    currentUserId,
    isEditing,
    setIsEditing,
    editTitle,
    setEditTitle,
    editDescription,
    setEditDescription,
    editStatusId,
    setEditStatusId,
    editAssignee,
    setEditAssignee,
    editDueDate,
    setEditDueDate,
    editPriority,
    setEditPriority,
    datePickerOpen,
    setDatePickerOpen,
    newAnimalSearch,
    setNewAnimalSearch,
    newInteractionSearch,
    setNewInteractionSearch,
    animalSearchResults,
    interactionSearchResults,
    updateMutation,
    archiveMutation,
    isArchived,
    handleSave,
  } = useTaskDetailData({
    taskId,
    boardIdOverride: boardIdProp,
    onArchiveSuccess: onClose,
  });

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/tasks/${taskId}/duplicate`);
    },
    onSuccess: (data: { task: { id: string; type?: string } }) => {
      invalidateTaskQueries();
      const bt = boardTypeFromContext(fromContext);
      const entityLabel = getBoardEntityLabel(data.task.type || bt);
      toast({ title: `${entityLabel} duplicated`, description: "A copy has been created." });
      const newId = data.task.id;
      const targetRoute = getBoardRoutePath(data.task.type || bt);
      setLocation(`${targetRoute}/${newId}`);
    },
    onError: () => {
      toast({ variant: "destructive", title: "Duplicate failed", description: "Could not duplicate this task." });
    },
  });

  const boardType = task?.type || boardTypeFromContext(fromContext);
  const config = getBoardTypeConfig(boardType);
  const isMeetingContext = boardType === "meeting";
  const exportSettings = (task?.exportSettings as ExportSettings | null) ?? DEFAULT_EXPORT_SETTINGS;
  const formats = exportSettings.formats;

  function getNotesContent(): string | null {
    const cached = queryClient.getQueryData<{ note: { content: string } | null }>(["/api/notes", "task", taskId]);
    return cached?.note?.content ?? null;
  }

  function buildExportData() {
    if (!task) return null;
    return { task, notes: getNotesContent(), isMeeting: isMeetingContext, boardType };
  }

  const incompleteFields = task ? getMeetingIncompleteFields(
    { task, notes: getNotesContent(), isMeeting: isMeetingContext, boardType },
    exportSettings,
  ) : [];

  async function copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      return success;
    } catch {
      return false;
    }
  }

  const handleCopyLink = async () => {
    if (!task) return;
    const dateStr = task.dueDate
      ? new Date(task.dueDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "No date set";
    const url = `${window.location.origin}${config.routePath}/${taskId}`;
    const summary = `${task.title} - ${dateStr} - ${url}`;
    const ok = await copyToClipboard(summary);
    setShareDialogOpen(false);
    toast(ok
      ? { title: "Link copied", description: "Title, date, and link copied to clipboard." }
      : { variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard." }
    );
  };

  const handleCopyMarkdown = async () => {
    const data = buildExportData();
    if (!data) return;
    const md = formatMeetingMarkdown(data, exportSettings);
    const ok = await copyToClipboard(md);
    setShareDialogOpen(false);
    toast(ok
      ? { title: "Markdown copied", description: `${config.labels.entity} copied as Markdown.` }
      : { variant: "destructive", title: "Copy failed", description: "Unable to copy to clipboard." }
    );
  };

  const handleDownloadPdf = () => {
    const data = buildExportData();
    if (!data) return;
    generateMeetingPdf(data, exportSettings);
    setShareDialogOpen(false);
    toast({ title: "PDF downloaded", description: `${config.labels.entity} saved as PDF.` });
  };

  return (
    <div className="flex flex-col h-full" data-testid="task-preview-panel">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 p-3 border-b bg-background">
        {isLoading || !task ? (
          <div className="h-5 bg-muted rounded animate-pulse flex-1" />
        ) : (
          <div className="flex items-baseline gap-2 flex-1 min-w-0">
            <h3 className="text-sm font-semibold truncate" data-testid="task-text-panel-title">
              {task.title}
            </h3>
            {isMeetingContext && task.dueDate && (
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0" data-testid="task-text-panel-meeting-date">
                {new Date(task.dueDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {task && (canEdit || isMeetingContext || hasCopyableBoards) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="task-button-actions-menu">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && !isEditing && (
                  <DropdownMenuItem onClick={() => setIsEditing(true)} data-testid="task-button-edit-task">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit {config.labels.entity}
                  </DropdownMenuItem>
                )}
                {canEdit && !task.archivedAt && (
                  <DropdownMenuItem
                    onClick={() => duplicateMutation.mutate()}
                    disabled={duplicateMutation.isPending}
                    data-testid="task-button-duplicate-panel"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                {canEdit && !task.archivedAt && hasCopyableBoards && (
                  <DropdownMenuItem onClick={() => setCopyToBoardOpen(true)} data-testid="task-button-copy-to-board">
                    <CopyPlus className="w-4 h-4 mr-2" />
                    Copy to Board
                  </DropdownMenuItem>
                )}
                {isMeetingContext && (
                  <DropdownMenuItem onClick={() => setShareDialogOpen(true)} data-testid="task-button-share-panel">
                    <Share className="w-4 h-4 mr-2" />
                    Share
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={`Open full ${task ? config.labels.entityLower : "task"} view`}
            onClick={() => setLocation(`${config.routePath}/${taskId}`)}
            data-testid="task-button-open-full-page"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="task-button-close-panel"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {isLoading || !task ? (
        <div className="p-4 space-y-4">
          <div className="h-6 bg-muted rounded animate-pulse w-3/4" />
          <div className="h-20 bg-muted rounded animate-pulse" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {task.isTemplate && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted border border-border" data-testid="task-banner-template">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium">Template</span>
            </div>
          )}
          {isEditing && canEdit && (
            <div className="flex items-start justify-between gap-2">
              <Input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="text-lg font-semibold"
                maxLength={500}
                data-testid="task-input-edit-title"
              />
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending} data-testid="task-button-save-task">
                  <Save className="w-3 h-3 mr-1" />Save
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} data-testid="task-button-cancel-edit">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {isEditing && canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={editStatusId} onValueChange={setEditStatusId}>
                <SelectTrigger className="w-36" data-testid="task-select-edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activeStatuses.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={editAssignee ?? "__unassigned__"} onValueChange={v => setEditAssignee(v === "__unassigned__" ? null : v)}>
                <SelectTrigger className="w-40" data-testid="task-select-edit-assignee">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {members.map(m => (
                    <SelectItem key={m.userId} value={m.userId}>{getDisplayName(m.firstName, m.lastName, m.username)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={editPriority ?? "none"} onValueChange={v => setEditPriority(v === "none" ? null : v)}>
                <SelectTrigger className="w-36" data-testid="task-select-edit-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 items-center text-sm">
              {task.type && task.type !== "regular" && (
                <>
                  <span className="text-xs text-muted-foreground text-right">Type</span>
                  <Badge variant="outline" className="capitalize w-fit" data-testid="task-badge-type">{task.type}</Badge>
                </>
              )}
              <span className="text-xs text-muted-foreground text-right">Status</span>
              <TaskStatusSelect
                taskId={task.id}
                currentStatusId={task.statusId}
                currentStatusName={task.status?.name ?? ""}
                statuses={activeStatuses}
                canEdit={canEdit}
                hideLabel
              />
              <span className="text-xs text-muted-foreground text-right">
                {config.labels.assigneeShort}
              </span>
              <TaskAssigneeSelect
                taskId={task.id}
                assignee={task.assignee}
                members={members}
                canEdit={canEdit}
                boardType={boardType}
                hideLabel
              />
              <span className="text-xs text-muted-foreground text-right">Priority</span>
              <TaskPrioritySelect
                taskId={task.id}
                currentPriority={task.priority}
                canEdit={canEdit}
                hideLabel
              />
              <span className="text-xs text-muted-foreground text-right self-start mt-0.5">Labels</span>
              <TaskLabelSection
                taskId={task.id}
                assignedLabels={task.labels}
                availableLabels={availableLabels}
                canEdit={canEdit}
                variant="inline"
                hideLabel
              />
            </div>
          )}

          {task.source && task.source !== "template" && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="task-provenance">
              <Link2 className="w-3.5 h-3.5" />
              {task.source === "animal" && task.animals.length > 0 ? (
                <span>Created from: <Link href={`/animals/${task.animals[0].animalId}`} className="underline hover-elevate" data-testid="task-provenance-link">{task.animals[0].animalName ?? task.animals[0].animalId}</Link></span>
              ) : task.source === "interaction" && task.interactions.length > 0 ? (
                <span>Created from: {task.interactions[0].eventType ?? "Interaction"}{task.interactions[0].occurredAt && ` — ${new Date(task.interactions[0].occurredAt).toLocaleDateString()}`}</span>
              ) : task.source === "animal" ? (
                <span>Created from animal</span>
              ) : task.source === "interaction" ? (
                <span>Created from interaction</span>
              ) : (
                <span>Created from {task.source}</span>
              )}
            </div>
          )}

          <Separator />

          <TaskDueDatePicker
            date={editDueDate}
            onChange={setEditDueDate}
            open={datePickerOpen}
            onOpenChange={setDatePickerOpen}
            canEdit={canEdit}
            isEditing={isEditing}
            isOverdue={isOverdue}
            dueDate={task.dueDate}
            boardType={boardType}
          />

          <div className="space-y-1">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{config.labels.description}</Label>
            {isEditing && canEdit ? (
              <Textarea
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                placeholder="Add a description..."
                className="resize-none"
                maxLength={5000}
                data-testid="task-textarea-edit-description"
              />
            ) : (
              <p className="text-sm text-foreground whitespace-pre-wrap" data-testid="task-text-description">
                {task.description || <span className="text-muted-foreground italic">No description</span>}
              </p>
            )}
          </div>

          <Separator />

          <TaskAnimalLinker
            taskId={task.id}
            animals={task.animals}
            canEdit={canEdit}
            animalSearchQuery={newAnimalSearch}
            setAnimalSearchQuery={setNewAnimalSearch}
            animalSearchResults={animalSearchResults}
          />

          <TaskInteractionLinker
            taskId={task.id}
            interactions={task.interactions}
            canEdit={canEdit}
            interactionSearchQuery={newInteractionSearch}
            setInteractionSearchQuery={setNewInteractionSearch}
            interactionSearchResults={interactionSearchResults}
          />

          <TaskChecklistSection
            taskId={task.id}
            checklistItems={task.checklistItems}
            canEdit={canEdit}
            boardType={boardType}
          />

          <Separator />

          <TaskLinkSection
            taskId={task.id}
            links={task.links ?? []}
            canEdit={canEdit}
          />

          <Separator />

          <TaskAttachmentsSection
            taskId={task.id}
            canEdit={canEdit}
          />

          <Separator />

          <TaskNotesSummary
            taskId={task.id}
            fromContext={fromContext}
          />

          <Separator />

          <CommentSection
            entityType="task"
            entityId={task.id}
            canComment={canComment}
            currentUserId={currentUserId}
            mentionableUsers={members}
          />

          <Separator />

          <div data-testid="task-activity-section">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1 mb-3">
              <History className="w-3 h-3" /> Activity
            </Label>
            <ActivityFeed
              activity={activityData?.activity ?? []}
              isLoading={activityLoading}
              showTaskTitle={false}
              maxItems={5}
            />
          </div>

          <Separator />

          <div className="space-y-2 text-xs text-muted-foreground pb-4">
            <div className="flex flex-wrap gap-4">
              <span data-testid="task-text-created-info">
                Created {new Date(task.createdAt).toLocaleString()}
                {task.creator && ` by ${formatUserName(task.creator)}`}
                {task.source === "template" ? ` from template${task.sourceTemplateName ? `: ${task.sourceTemplateName}` : ""}` : ""}
              </span>
              <span data-testid="task-text-updated-info">
                Updated {new Date(task.updatedAt).toLocaleString()}
                {task.updater && ` by ${formatUserName(task.updater)}`}
              </span>
            </div>
            {canEdit && !isArchived && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    disabled={archiveMutation.isPending}
                    data-testid="task-button-archive-task"
                  >
                    <Archive className="w-3 h-3 mr-1" />
                    Archive {config.labels.entity}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{config.labels.archiveTitle}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {config.labels.archiveDescription}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="button-cancel-archive">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => archiveMutation.mutate()} data-testid="button-confirm-archive">
                      Archive
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {canEdit && isArchived && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending}
                data-testid="task-button-unarchive-task"
              >
                <Archive className="w-3 h-3 mr-1" />
                {config.labels.unarchiveAction}
              </Button>
            )}
          </div>
        </div>
      )}

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent data-testid="share-panel-dialog">
          <DialogHeader>
            <DialogTitle>Share {config.labels.entity}</DialogTitle>
          </DialogHeader>

          {incompleteFields.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-300" data-testid="text-share-panel-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Incomplete {config.labels.entityLower}: {incompleteFields.join(", ")}</span>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {formats.copyLink && (
              <div>
                <Button variant="outline" className="justify-start gap-2 w-full" onClick={handleCopyLink} data-testid="button-panel-copy-link">
                  <Link2 className="w-4 h-4" />
                  Copy Link
                </Button>
                <p className="text-xs text-muted-foreground mt-1 ml-1" data-testid="text-panel-copy-link-note">
                  Only BETI users with access to this {config.labels.entityLower.split(" ")[0]} board can view this {config.labels.entityLower}.
                </p>
              </div>
            )}
            {formats.copyMarkdown && (
              <Button variant="outline" className="justify-start gap-2" onClick={handleCopyMarkdown} data-testid="button-panel-copy-markdown">
                <Copy className="w-4 h-4" />
                Copy as Markdown
              </Button>
            )}
            {formats.downloadPdf && (
              <Button variant="outline" className="justify-start gap-2" onClick={handleDownloadPdf} data-testid="button-panel-download-pdf">
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {task && (
        <CopyToBoardDialog
          open={copyToBoardOpen}
          onOpenChange={setCopyToBoardOpen}
          taskId={taskId}
          sourceBoardId={boardIdProp}
          boardType={boardType}
          entityLabel={config.labels.entity}
        />
      )}
    </div>
  );
}
